import React, { useCallback, useMemo } from 'react'
import { matchPath } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { accountingApiEvents, accountingApiSelectors } from '../../../api/accounting'
import { subscribe } from 'react-contextual'
import { path, pathOr } from 'ramda'
import { ROUTES } from 'constants/routes'
import { ContactsProvider } from '../../../contacts'
import ViewStatementProvider from '../ViewStatement/ViewStatementProvider'
import ViewStatement from '../ViewStatement/ViewStatement'
import { match as RouterMatch } from 'react-router-dom'
import { $TSFixMe } from 'types/ts-migrate'
import { RootState } from 'src/state/types'

type StatementFormat = 'json' | 'pdf' | 'csv'

const STATEMENT_EVENTS = {
  json: accountingApiEvents.consolidatedStatementJson_request,
  pdf: accountingApiEvents.consolidatedStatementPdf_request,
  csv: accountingApiEvents.consolidatedStatementCsv_request,
}

interface ConsolidatedCustomerStatementProps {
  match: RouterMatch
  redirect: (path: string) => void
  contactFilterSearch: string
}

const ConsolidatedCustomerStatement = ({
  match,
  redirect,
  contactFilterSearch,
}: ConsolidatedCustomerStatementProps): null | React.ReactElement => {
  const editMatchPath = matchPath(match.url, {
    path: ROUTES.contactConsolidatedStatement,
    exact: true,
    strict: false,
  })

  const partyId: $TSFixMe = path(['params', 'id'], editMatchPath)
  const dispatch = useDispatch()

  const fetchConsolidatedStatement = useCallback(
    (startDate: string, endDate: string, format: StatementFormat = 'json') => {
      if (partyId) {
        dispatch(
          STATEMENT_EVENTS[format]({
            partyId,
            start_date: startDate,
            end_date: endDate,
          }),
        )
      }
    },
    [partyId, dispatch],
  )

  const fetchConsolidatedStatementPdf = useCallback(
    (startDate: string, endDate: string) => fetchConsolidatedStatement(startDate, endDate, 'pdf'),
    [fetchConsolidatedStatement],
  )
  const fetchConsolidatedStatementCsv = useCallback(
    (startDate: string, endDate: string) => fetchConsolidatedStatement(startDate, endDate, 'csv'),
    [fetchConsolidatedStatement],
  )
  const statementData = useSelector((state: RootState) =>
    accountingApiSelectors.getConsolidatedStatementJsonById(state)(partyId),
  )
  const statementPdf = useSelector((state: RootState) =>
    accountingApiSelectors.getConsolidatedStatementPdfById(state)(partyId),
  )
  const statementCsv = useSelector((state: RootState) =>
    accountingApiSelectors.getConsolidatedStatementCsvById(state)(partyId),
  )

  const isCompany = pathOr(false, ['context', 'customer', 'isCompany'], statementData)
  const fileName = isCompany
    ? pathOr('Consolidated Statement', ['context', 'customer', 'companyName'], statementData)
    : pathOr('Consolidated Statement', ['context', 'customer', 'name'], statementData)

  const isOpen = !!partyId

  return useMemo(() => {
    return isOpen ? (
      <ViewStatementProvider
        onClose={() => redirect(`/contacts/${partyId}${contactFilterSearch}`)}
        onParamChange={fetchConsolidatedStatement}
        isOpen={isOpen}
        statementData={statementData}
        downloadStatement={fetchConsolidatedStatementPdf}
        statementPdf={statementPdf}
        pdfFileName={fileName}
        downloadStatementCsv={fetchConsolidatedStatementCsv}
        statementCsv={statementCsv}
        csvFileName={fileName}
      >
        <ViewStatement title="Consolidated Statement" />
      </ViewStatementProvider>
    ) : null
  }, [
    redirect,
    contactFilterSearch,
    partyId,
    fetchConsolidatedStatement,
    fetchConsolidatedStatementPdf,
    fetchConsolidatedStatementCsv,
    statementData,
    statementPdf,
    statementCsv,
    isOpen,
    fileName,
  ])
}

export default subscribe([ContactsProvider], ({ match, redirect, contactFilterSearch }: $TSFixMe) => ({
  match,
  redirect,
  contactFilterSearch,
}))(ConsolidatedCustomerStatement)
